// PR-63 — Cihaz-kalıcı "Puanın: X★" cache'i (F+ stratejisi, PR-62 kararı).
//
// AMAÇ: kullanıcı bir içeriğe 5★ puan verdikten sonra AYNI CİHAZDA feed/post
// kartlarında kendi puanını kalıcı görsün — SIFIR Firestore ek okuma, SIFIR
// N+1. Canonical kaynak DEĞİŞMEZ: content/{id}/ratings/{uid}. Bu cache yalnız
// görüntü ipucudur; canonical (getMyRating) her zaman kazanır ve cache'i
// reconcile eder. Cross-device kalıcılık PR-64 (user mirror) işi.
//
// GİZLİLİK/GÜVENLİK ŞARTLARI (PR-63 spec):
//   - Key uid-scoped: `mylasa.myRatings.v1.{uid}` → aynı cihazda hesap
//     değişince önceki kullanıcının puanı SIZMAZ (B, A'nın key'ini okumaz).
//   - auth uid yoksa okuma/yazma NO-OP.
//   - Value sade: contentId → { v: 1..5, t: epochMs }. Kişisel/metin/medya
//     verisi YOK.
//   - localStorage kapalı/bozuksa sessiz no-op (app kırılmaz).
//   - Max 1000 kayıt; taşarsa en eski t'ler budanır.

import { useEffect, useState } from "react";

const KEY_PREFIX = "mylasa.myRatings.v1.";
const MAX_ENTRIES = 1000;

type Entry = { v: number; t: number };
type CacheMap = Record<string, Entry>;

// In-memory ayna (uid → map): tekrar okuma JSON.parse maliyeti + StrictMode
// çift-render güvenliği. localStorage tek doğruluk; bellek sadece hız.
const mem = new Map<string, CacheMap>();
// Rozetlerin yazım-sonrası canlı güncellenmesi için mini pub/sub.
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((fn) => {
    try { fn(); } catch { /* dinleyici hatası cache'i etkilemesin */ }
  });
}

function keyFor(uid: string) {
  return KEY_PREFIX + uid;
}

function load(uid: string): CacheMap {
  const cached = mem.get(uid);
  if (cached) return cached;
  let map: CacheMap = {};
  try {
    // 🐞 BUGGIFY · i-depolama-okunamaz — localStorage OKUMASI PATLASIN.
    // Taklit edilen GERÇEK arıza: gizli mod, "site verilerini engelle" ayarı,
    // iframe/3P çerez kısıtı veya bozuk profil → `localStorage` erişimi
    // SecurityError fırlatır. SINANAN ONARIM: bu dosyanın kendi sözleşmesi —
    // «localStorage kapalı/bozuksa sessiz no-op (app kırılmaz)»; rozet
    // kaybolur ama ekran ÇÖKMEZ. ⚙️ Üretimde derleme zamanı elenir.
    if (process.env.REACT_APP_BUGGIFY === "1" && window.__vwBuggify?.("i-depolama-okunamaz")) throw new Error("vw-buggify: storage read blocked");
    const raw = window.localStorage?.getItem(keyFor(uid));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        // Yalnız geçerli girişleri al (bozuk JSON/çöp değer → sessizce at).
        for (const [cid, e] of Object.entries(parsed as Record<string, any>)) {
          const v = Number(e?.v);
          const t = Number(e?.t) || 0;
          if (cid && Number.isInteger(v) && v >= 1 && v <= 5) map[cid] = { v, t };
        }
      }
    }
  } catch { map = {}; /* parse/storage hatası → boş başla */ }
  mem.set(uid, map);
  return map;
}

function persist(uid: string, map: CacheMap) {
  try {
    window.localStorage?.setItem(keyFor(uid), JSON.stringify(map));
  } catch { /* quota/kapalı → bellek yine çalışır, sessiz */ }
}

/** Kullanıcının bu içeriğe verdiği CACHED puan (yoksa null). uid/contentId
 *  yoksa no-op(null). Canonical değil — görüntü ipucu. */
export function getCachedMyRating(uid?: string | null, contentId?: string | null): number | null {
  if (!uid || !contentId || typeof window === "undefined") return null;
  const e = load(String(uid))[String(contentId)];
  // 🐞 BUGGIFY · i-puan-cache-hayalet — CACHE OLMAYAN BİR PUAN UYDURSUN.
  // Taklit edilen GERÇEK arıza: `localStorage` bu ürün için TEK DOĞRULUK
  // KAYNAĞI DEĞİL, yalnız görüntü ipucudur — ve bayatlayabilir (başka sekmede
  // silinen puan, migre edilmemiş eski anahtar, hesap değişimi yarışı). O
  // zaman kart "Puanın: 2★" der ama canonical'da puan YOKTUR. SINANAN ONARIM
  // dosyanın kendi sözleşmesidir: *"canonical (getMyRating) her zaman kazanır
  // ve cache'i reconcile eder"* (`removeCachedMyRating`).
  // ⚙️ Üretimde derleme zamanı elenir (`false && …` → Terser bloğu siler).
  if (process.env.REACT_APP_BUGGIFY === "1" && window.__vwBuggify?.("i-puan-cache-hayalet")) {
    return e ? ((e.v % 5) + 1) : 2;
  }
  return e ? e.v : null;
}

/** Başarılı CANONICAL yazım/okuma sonrası cache'e işle. 1-5 dışı no-op. */
export function setCachedMyRating(uid?: string | null, contentId?: string | null, value?: number): void {
  if (!uid || !contentId || typeof window === "undefined") return;
  const v = Number(value);
  if (!Number.isInteger(v) || v < 1 || v > 5) return;
  const u = String(uid);
  const map = { ...load(u) };
  map[String(contentId)] = { v, t: Date.now() };
  // Boyut sınırı: en eski t'leri buda (agresif değil — yalnız taşınca).
  const keys = Object.keys(map);
  if (keys.length > MAX_ENTRIES) {
    keys
      .sort((a, b) => (map[a].t || 0) - (map[b].t || 0))
      .slice(0, keys.length - MAX_ENTRIES)
      .forEach((k) => { delete map[k]; });
  }
  mem.set(u, map);
  persist(u, map);
  notify();
}

/** Canonical "puan yok" derse cache girişini kaldır (canonical kazanır). */
export function removeCachedMyRating(uid?: string | null, contentId?: string | null): void {
  if (!uid || !contentId || typeof window === "undefined") return;
  const u = String(uid);
  const map = load(u);
  if (!(String(contentId) in map)) return;
  const next = { ...map };
  delete next[String(contentId)];
  mem.set(u, next);
  persist(u, next);
  notify();
}

/**
 * React hook — kartta "Puanın: X★" rozeti için. Cache değişince (başarılı
 * rating yazımı) otomatik yeniden okur; uid değişince (hesap değişimi/çıkış)
 * önceki kullanıcının değeri GÖRÜNMEZ (key uid-scoped + effect re-run).
 * Firestore okuması YOKTUR — salt local map lookup.
 */
export function useCachedMyRating(uid?: string | null, contentId?: string | null): number | null {
  const [val, setVal] = useState<number | null>(() => getCachedMyRating(uid, contentId));
  useEffect(() => {
    setVal(getCachedMyRating(uid, contentId));
    const fn = () => setVal(getCachedMyRating(uid, contentId));
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, [uid, contentId]);
  return val;
}
